<template>
  <modal :name="name" :width="560" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @closed="$emit('closed')" @opened="focusHeading">
    <div v-if="action" class="popup booking-manual-modal" :aria-busy="busy || uploading" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" :disabled="busy || uploading" :aria-label="`Close ${action.name}`" @click="close" />
      <header class="booking-manual-modal__header">
        <h2 ref="heading" class="popup__title" tabindex="-1">{{ action.name }}</h2>
        <p>{{ (booking.airline || {}).name }} · Booking #{{ booking.id }}</p>
      </header>
      <div class="popup__body">
        <section class="booking-manual-modal__explanation" aria-label="What will happen"><p>{{ action.explanation }}</p></section>
        <form novalidate @submit.prevent="submit">
          <fieldset :disabled="busy || uploading || completed || uncertain" class="booking-manual-modal__fields">
            <app-form-group v-if="hasField('pnr')" label="PNR" :label-for="`${name}-pnr`" :required="required('pnr')">
              <app-input :id="`${name}-pnr`" v-model="draft.pnr" name="pnr" placeholder="Enter PNR" :maxlength="100" :error="Boolean(fieldErrors.pnr)" @input="clearError('pnr')" />
              <template #error><div v-if="fieldErrors.pnr">{{ fieldErrors.pnr }}</div></template>
            </app-form-group>
            <template v-if="hasField('payment_time_limit')">
              <p class="booking-manual-modal__hint">Enter at least one deadline: payment or ticketing.</p>
              <div class="booking-manual-modal__deadlines">
                <app-form-group v-for="limit in deadlines" :key="limit.key" :label="limit.name" :label-for="`${name}-${limit.key}-date`" :required="required(limit.key)">
                  <booking-deadline-input :id="`${name}-${limit.key}`" v-model="draft[limit.key]" :label="limit.name" :disabled="busy || uploading || completed || uncertain" :error="Boolean(fieldErrors[limit.key] || fieldErrors.deadlines)" @input-valid="clearError(limit.key)" @input-error="invalidDate(limit.key)" />
                  <template #error><div v-if="fieldErrors[limit.key]">{{ fieldErrors[limit.key] }}</div></template>
                </app-form-group>
              </div>
              <p v-if="fieldErrors.deadlines" class="booking-manual-modal__field-error" role="alert">{{ fieldErrors.deadlines }}</p>
            </template>
            <app-form-group v-if="hasField('ticket')" label="Ticket attachment" :required="required('ticket')">
              <app-uploader :key="`${sessionId}-${uploaderRevision}`" v-model="draft.ticket" :single-file="true" :readonly="busy || uploading || completed || uncertain" @uploading="uploading = $event" @onAddedFile="ticketChanged" @upload-error="uploadError">
                <template #action="{ filePresent }"><div><button v-if="!filePresent" type="button" class="booking-manual-modal__drop dz-message"><i class="pi pi-upload" aria-hidden="true" /><span>Drop a file here or choose a file.</span></button></div></template>
                <template #file-icon><i class="pi pi-paperclip" aria-hidden="true" /></template>
                <template #remove-icon><i class="pi pi-times" aria-hidden="true" /><span class="booking-manual-modal__sr">Remove ticket file</span></template>
              </app-uploader>
              <p v-if="uploading" class="booking-manual-modal__hint" role="status">Uploading ticket…</p>
              <template #error><div v-if="fieldErrors.ticket">{{ fieldErrors.ticket }}</div></template>
            </app-form-group>
            <app-form-group :label="commentLabel" :label-for="`${name}-comment`" :required="required('comment')" class="booking-manual-modal__comment">
              <app-textarea :id="`${name}-comment`" v-model="draft.comment" name="comment" :placeholder="commentLabel === 'Reason' ? 'Enter the reason the flight is unavailable' : 'Enter a comment'" :error="Boolean(fieldErrors.comment)" @input="clearError('comment')" />
              <template #error><div v-if="fieldErrors.comment">{{ fieldErrors.comment }}</div></template>
            </app-form-group>
          </fieldset>
          <div v-if="error" class="booking-manual-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>{{ error }}</p></div>
          <div v-if="!permitted && !completed" class="booking-manual-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>This action is no longer available for this booking. Reload the booking before continuing.</p></div>
          <p v-if="completed" class="booking-manual-modal__success" role="status">Action completed. Reload booking details before continuing.</p>
          <div v-if="uncertain" class="booking-manual-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>The result could not be verified. Reload the booking and check its status before starting another action.</p></div>
          <footer class="booking-manual-modal__footer">
            <app-button v-if="completed || uncertain || !permitted" :disabled="busy || uploading" @click.native="reloadBooking">{{ reloading ? 'Updating…' : 'Reload booking' }}</app-button>
            <app-button v-else type="submit" :theme="action.tone === 'danger' ? 'error' : 'default'" :disabled="!canSubmit">{{ saving ? 'Saving…' : uploading ? 'Uploading…' : action.button }}</app-button>
          </footer>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import BookingDeadlineInput from '@/components/flights/BookingDeadlineInput.vue'
import { getFlightsBooking, postFlightsManualBookingAction } from '@/http'
import { bookingManualActions, manualActionDraft, manualActionPayload, manualActionErrors, manualActionState } from './booking-manual-actions'
import { responseError } from './booking-detail'

export default {
  name: 'BookingManualActionModal',
  components: { BookingDeadlineInput },
  props: { name: { type: String, required: true }, booking: { type: Object, required: true }, action: { type: Object, default: null } },
  data() {
    return { draft: {}, fieldErrors: {}, dateErrors: {}, error: '', saving: false, uploading: false, uploaderRevision: 0, reloading: false, completed: false, uncertain: false, sessionId: 0, initialState: '', deadlines: [{ key: 'payment_time_limit', name: 'Payment deadline' }, { key: 'ticketing_time_limit', name: 'Ticketing deadline' }] }
  },
  computed: {
    currentAction() { return this.action && bookingManualActions(this.booking).find(action => action.code === this.action.code) },
    permitted() { return Boolean(this.currentAction) },
    busy() { return this.saving || this.reloading },
    canSubmit() { return this.permitted && !this.busy && !this.uploading && !this.completed && !this.uncertain },
    commentLabel() { return this.action && this.action.code === 'mark_not_available' ? 'Reason' : 'Comment' }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    hasField(field) { return this.action && this.action.fields.includes(field) },
    required(field) { return Boolean(this.currentAction && this.currentAction.required_fields.includes(field)) },
    initialize() {
      this.sessionId++
      this.draft = manualActionDraft(this.booking)
      this.initialState = manualActionState(this.booking)
      this.fieldErrors = {}
      this.dateErrors = {}
      this.error = ''
      this.saving = false
      this.uploading = false
      this.reloading = false
      this.completed = false
      this.uncertain = false
    },
    clearError(field) {
      this.$delete(this.fieldErrors, field)
      this.$delete(this.dateErrors, field)
      if (field.endsWith('_time_limit')) this.$delete(this.fieldErrors, 'deadlines')
    },
    invalidDate(field) {
      const message = 'Enter a valid date and time.'
      this.$set(this.fieldErrors, field, message)
      this.$set(this.dateErrors, field, message)
    },
    uploadError(message) { this.$set(this.fieldErrors, 'ticket', message) },
    ticketChanged(file) {
      this.clearError('ticket')
      // Reset the single-file queue after removal or a failed upload so another file can be chosen.
      if (!file || !Object.keys(file).length) this.uploaderRevision++
    },
    async submit() {
      if (!this.canSubmit) return
      this.fieldErrors = { ...manualActionErrors(this.currentAction, this.draft), ...this.dateErrors }
      if (Object.keys(this.fieldErrors).length) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      const actionCode = this.action.code
      this.saving = true
      this.error = ''
      let sent = false
      try {
        const { data: current } = await getFlightsBooking(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        const allowed = bookingManualActions(current).find(action => action.code === actionCode)
        if (!current || current.id !== bookingId || !allowed || manualActionState(current) !== this.initialState) {
          if (current && current.id === bookingId) this.$emit('updated', current)
          this.error = 'The booking changed or this action is no longer available. Reopen the action with the latest booking details.'
          return
        }
        this.fieldErrors = { ...manualActionErrors(allowed, this.draft), ...this.dateErrors }
        if (Object.keys(this.fieldErrors).length) return
        sent = true
        const { data } = await postFlightsManualBookingAction(bookingId, actionCode, manualActionPayload(allowed, this.draft), { localErrors: true })
        if (sessionId !== this.sessionId) return
        this.completed = true
        this.$notify({ type: 'success', title: `${this.action.name} action completed.` })
        if (data && data.id === bookingId && Array.isArray(data.available_actions)) {
          this.$emit('updated', data)
          this.saving = false
          this.close()
        } else await this.reloadBooking()
      } catch (error) {
        if (sessionId === this.sessionId) {
          this.error = responseError(error)
          this.uncertain = sent && (!error.response || error.response.status >= 500)
        }
      } finally {
        if (sessionId === this.sessionId) this.saving = false
      }
    },
    async reloadBooking() {
      if (this.reloading || this.uploading) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.reloading = true
      this.error = ''
      try {
        const { data } = await getFlightsBooking(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        if (!data || data.id !== bookingId) throw new Error('Invalid booking response')
        this.$emit('updated', data)
        this.reloading = false
        this.saving = false
        this.close()
      } catch (error) {
        if (sessionId === this.sessionId) this.error = responseError(error, 'Unable to reload booking details.')
      } finally {
        if (sessionId === this.sessionId) this.reloading = false
      }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.busy && !this.uploading) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.busy || this.uploading) event.cancel()
      else this.sessionId++
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-manual-modal { color: #20241d; font: 400 15px/1.5 $font-family-base; }
.booking-manual-modal__header { padding-right: 32px; margin-bottom: 20px; }
.booking-manual-modal .popup__title { margin-bottom: 6px; font-size: 20px; font-weight: 500; text-align: left; cursor: default; }
.booking-manual-modal__header > p { margin: 0; color: $color-text-second; font-size: 13px; }
.booking-manual-modal__explanation { padding: 16px; border: 1px solid $color-border; border-radius: 6px; background: #f7f8f5; }
.booking-manual-modal__explanation p { margin: 0; }
.booking-manual-modal__fields { min-width: 0; margin: 20px 0 0; padding: 0; border: 0; }
.booking-manual-modal__fields:disabled { opacity: .7; }
.booking-manual-modal__deadlines { display: grid; grid-template-columns: minmax(0, 1fr); gap: 0 16px; }
.booking-manual-modal__deadlines > * { min-width: 0; }
.booking-manual-modal__hint { margin: 0 0 16px; color: $color-text-second; font-size: 13px; }
.booking-manual-modal__comment { margin-bottom: 0; }
.booking-manual-modal__field-error { color: $color-error; }
.booking-manual-modal__field-error { margin: -10px 0 20px; font-size: 13px; }
.booking-manual-modal__drop { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; width: 100%; min-height: 120px; padding: 20px; border: 1px dashed $color-border; border-radius: 6px; background: #fafafa; color: $color-text-second; font: inherit; font-size: 14px; cursor: pointer; }
.booking-manual-modal__drop > .pi { color: $color-theme; font-size: 24px; }
.booking-manual-modal__drop:hover { border-color: $color-theme; background: #f7f8f5; }
.booking-manual-modal__sr { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.booking-manual-modal__error { display: flex; align-items: flex-start; gap: 10px; margin-top: 16px; padding: 14px; border: 1px solid #efd4d1; border-radius: 6px; background: #fff5f3; color: #993c35; font-size: 14px; overflow-wrap: anywhere; }
.booking-manual-modal__error > .pi { flex-shrink: 0; margin-top: 2px; font-size: 18px; }
.booking-manual-modal__error p { min-width: 0; margin: 0; }
.booking-manual-modal__success { color: #527b12; }
.booking-manual-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; padding-top: 18px; border-top: 1px solid $color-border; }
.booking-manual-modal__footer .btn { min-width: auto; }
.booking-manual-modal .popup__close:focus-visible, .booking-manual-modal__drop:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
@media (max-width: 520px) { .booking-manual-modal__deadlines { grid-template-columns: minmax(0, 1fr); } }
</style>
